/**
 * ActivationCodeStep - Step 1 of wizard
 *
 * Handles activation code input and verification
 */

import { useState, useCallback } from 'react';
import { getEnv } from '@/lib/env';
import { useFormContext } from 'react-hook-form';
import type { StepProps, Step1Response } from '../types';
import type { ActivationCodeFormData } from '../validation/schemas';
import { useSettings } from '@/contexts/SettingsContext';
import { ACTIVATION_CODE_STEP } from '@/constants/translations';
import { OtpInput } from '@/components/Auth/OtpInput';
import './ActivationCodeStep.css';

interface ActivationCodeStepProps extends StepProps {
  sessionId: string;
  codeLength?: number;
  isAlphanumeric?: boolean;
}

export function ActivationCodeStep({ sessionId, onNext, codeLength = 4, isAlphanumeric = false }: ActivationCodeStepProps) {
  const { settings } = useSettings();
  const t = ACTIVATION_CODE_STEP[settings.language];

  const {
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    setError,
  } = useFormContext<ActivationCodeFormData>();

  const [otpCode, setOtpCode] = useState('');
  const [apiError, setApiError] = useState<string>('');

  const handleCodeChange = useCallback((code: string) => {
    setOtpCode(code);
    setValue('activationCode', code, { shouldValidate: code.length === codeLength });
  }, [setValue, codeLength]);

  /**
   * Handle form submission
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = async (_: ActivationCodeFormData) => {
    if (otpCode.length !== codeLength) {
      setApiError(t.errors.invalidCode);
      return;
    }

    try {
      setApiError('');

      const apiUrl = getEnv('API_URL') || 'http://localhost:43011';
      const response = await fetch(
        `${apiUrl}/patient/auth/session/${sessionId}/verify-activation-step1`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            activation_code: otpCode,
          }),
        }
      );

      const result: Step1Response = await response.json();

      if (!response.ok || !result.success) {
        // Handle API errors
        const errorMessage = getErrorMessage(result.error);
        setApiError(errorMessage);
        setError('activationCode', { message: errorMessage });
        return;
      }

      // Success - proceed to next step
      await onNext(result);
    } catch (error) {
      console.error('[ActivationCodeStep] Error:', error);
      setApiError(t.errors.network);
    }
  };

  /**
   * Convert API error codes to user-friendly messages
   */
  const getErrorMessage = (error?: string): string => {
    switch (error) {
      case 'INVALID_CODE':
        return t.errors.invalidCode;
      case 'SESSION_NOT_FOUND':
        return t.errors.surveyNotFound;
      case 'SESSION_EXPIRED':
        return t.errors.expired;
      default:
        return t.errors.authFailed;
    }
  };

  return (
    <div className="activation-code-step">
      <div className="step-card">
        <h2 className="step-title">{t.title}</h2>
        <p className="step-description">
          {t.description(codeLength)}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="step-form">
          <div className="form-group">
            <label className="form-label">{t.label}</label>
            <OtpInput
              length={codeLength}
              alphanumeric={isAlphanumeric}
              onComplete={handleCodeChange}
              onChange={handleCodeChange}
              disabled={isSubmitting}
              error={(errors.activationCode?.message || apiError) ?? undefined}
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || otpCode.length !== codeLength}
          >
            {isSubmitting ? t.submitting : t.next}
          </button>
        </form>

        <div className="step-help">
          <p>{t.helpText1}</p>
          <p>{t.helpText2}</p>
        </div>
      </div>
    </div>
  );
}
